import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';
import CountdownTimer from '../../../components/feature/CountdownTimer';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    { question: 'Qual a diferença entre contratar seguros com a IBX Brasil e contratar diretamente?', answer: 'Ao contratar diretamente, você aceita as condições padrão da seguradora. Com a IBX Brasil, você tem análise técnica profissional, negociação estratégica para reduzir custos, gestão completa de contratos e suporte especializado em sinistros. Nossos clientes economizam em média 35% e têm coberturas mais adequadas.' },
    { question: 'Como a IBX Brasil consegue reduzir os custos com seguros?', answer: 'Utilizamos três estratégias principais: 1) Análise técnica para eliminar coberturas desnecessárias e ajustar valores; 2) Negociação estratégica usando nosso relacionamento e volume com seguradoras; 3) Gestão contínua para identificar oportunidades de otimização. Isso resulta em economia média de 35% sem comprometer a proteção.' },
    { question: 'A IBX Brasil atende apenas empresas ou também pessoas físicas?', answer: 'Atendemos tanto empresas quanto pessoas físicas. Para empresas, oferecemos gestão completa de riscos corporativos. Para pessoas físicas, profissionalizamos a contratação de seguros pessoais como automóvel, residencial e vida, garantindo economia e proteção adequada.' },
    { question: 'Como funciona a análise gratuita?', answer: 'A análise gratuita é um diagnóstico inicial onde avaliamos seus seguros atuais, identificamos oportunidades de economia e riscos não cobertos. Não há compromisso de contratação. Após a análise, apresentamos um relatório com recomendações estratégicas e potencial de economia.' },
    { question: 'Quanto tempo leva o processo de contratação?', answer: 'O processo completo leva de 7 a 15 dias úteis, dependendo da complexidade. Inclui: diagnóstico inicial (2-3 dias), cotação e negociação com seguradoras (3-5 dias), análise técnica das propostas (2-3 dias) e formalização do contrato (2-4 dias). Casos urgentes podem ser priorizados.' },
    { question: 'A IBX Brasil cobra alguma taxa adicional?', answer: 'Não cobramos taxas adicionais dos clientes. Nossa remuneração vem das seguradoras através de comissões padrão de mercado, já incluídas no prêmio do seguro. Ou seja, você tem gestão profissional sem custo extra, apenas economia.' },
    { question: 'O que acontece se eu precisar acionar o seguro?', answer: 'Oferecemos suporte técnico completo em sinistros. Nossa equipe acompanha todo o processo: abertura do sinistro, documentação necessária, negociação com a seguradora e acompanhamento até o pagamento da indenização. Isso garante agilidade e valores justos.' },
    { question: 'Posso cancelar meus seguros atuais para contratar com a IBX Brasil?', answer: 'Sim, mas recomendamos aguardar a renovação para evitar multas contratuais. Fazemos a transição estratégica: analisamos seus contratos atuais, planejamos a migração e garantimos que não haja períodos descobertos. Em casos específicos, o cancelamento antecipado pode valer a pena pela economia gerada.' }
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: accordionRef, visibleItems } = useStaggerAnimation(faqs.length);
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section className="py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-petrol-50 to-white" aria-labelledby="faq-heading">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-petrol-100 px-4 py-2 rounded-full mb-4 sm:mb-6" aria-hidden="true">
            <i className="ri-question-line text-petrol-700 text-base sm:text-lg"></i>
            <span className="text-petrol-800 font-bold text-xs sm:text-sm uppercase tracking-wide">Perguntas Frequentes</span>
          </div>
          <h2 id="faq-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-900 mb-4 sm:mb-6 px-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Dúvidas Sobre{' '}
            <span className="text-strategic-green-600">Gestão de Seguros</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-petrol-800 leading-relaxed px-4">
            Respostas claras para as principais dúvidas sobre contratação profissional de seguros e gestão de riscos.
          </p>
        </div>

        <div ref={accordionRef as React.RefObject<HTMLDivElement>} className="space-y-3 sm:space-y-4 mb-12 sm:mb-16">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl sm:rounded-2xl shadow-lg border border-petrol-100 overflow-hidden transition-all duration-700 ease-out ${visibleItems[index] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left hover:bg-petrol-50 transition-colors cursor-pointer min-h-[44px]"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
                id={`faq-question-${index}`}
              >
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-petrol-900 flex-1">{faq.question}</h3>
                <i
                  className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl sm:text-3xl text-strategic-green-600 flex-shrink-0 transition-transform`}
                  aria-hidden="true"
                ></i>
              </button>
              {openIndex === index && (
                <div
                  id={`faq-answer-${index}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0"
                >
                  <p className="text-sm sm:text-base text-petrol-800 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mb-8 sm:mb-12">
          <CountdownTimer location="faq" />
        </div>

        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`bg-gradient-to-r from-petrol-900 to-petrol-700 rounded-xl sm:rounded-2xl p-6 sm:p-10 text-center shadow-2xl transition-all duration-700 ease-out ${ctaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
        >
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 px-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ainda Tem Dúvidas?
          </h3>
          <p className="text-base sm:text-lg lg:text-xl text-gray-100 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Fale diretamente com nossos especialistas e tire todas as suas dúvidas sobre gestão de riscos e seguros.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center px-4">
            <a
              href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20tenho%20d%C3%BAvidas%20sobre%20gest%C3%A3o%20de%20seguros."
              target="_blank"
              rel="nofollow noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 bg-strategic-green-500 text-white px-6 sm:px-8 py-4 sm:py-5 rounded-xl text-base sm:text-lg font-bold hover:bg-strategic-green-600 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              aria-label="Falar com especialista no WhatsApp"
            >
              <i className="ri-whatsapp-line text-xl sm:text-2xl" aria-hidden="true"></i>
              Falar no WhatsApp
            </a>
            <Link
              to="/contato"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 bg-white text-petrol-900 px-6 sm:px-8 py-4 sm:py-5 rounded-xl text-base sm:text-lg font-bold hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              aria-label="Enviar mensagem para a IBX Brasil"
            >
              <i className="ri-mail-line text-xl" aria-hidden="true"></i>
              Enviar Mensagem
            </Link>
          </div>
        </div>

      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
          }))
        })}
      </script>
    </section>
  );
}
